// https://www.radix-ui.com/primitives/docs/components/accordion
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import type { ReactNode } from "react";
import { forwardRef } from "react";
import cx from "ui/cx";
import IconChevronDown from "ui/icons/IconMiniChevronDown";
import styles from "./Accordion.module.scss";

interface AccordianPropsBase extends Omit<React.ComponentProps<typeof AccordionPrimitive.Root>, "asChild" | "type"> {
  children: ReactNode;
  className?: string;
  fullWidth?: boolean; // Sets border left/right to 0;
}

export interface AccordionSingleProps extends AccordianPropsBase {
  type: "single";
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  collapsible?: boolean;
}

export interface AccordionMultipleProps extends AccordianPropsBase {
  type: "multiple";
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
}

export type AccordionProps = AccordionSingleProps | AccordionMultipleProps;

export interface AccordionItemProps extends Omit<React.ComponentProps<typeof AccordionPrimitive.Item>, "asChild"> {
  children: ReactNode;
  className?: string;
  header?: ReactNode;
}

export interface AccordionHeaderProps extends Omit<React.ComponentProps<typeof AccordionPrimitive.Header>, "asChild"> {
  children: ReactNode;
  className?: string;
}

export interface AccordionTriggerProps
  extends Omit<React.ComponentProps<typeof AccordionPrimitive.Trigger>, "asChild"> {
  children: ReactNode;
  className?: string;
}

export interface AccordionContentProps
  extends Omit<React.ComponentProps<typeof AccordionPrimitive.Content>, "asChild"> {
  children: ReactNode;
  className?: string; // Gets applied to the contentText div
}

function AccordionRoot(props: AccordionProps) {
  const { children, disabled = false, className, fullWidth = false, ...restProps } = props;

  const handleValueChange = (newValue: string | string[]) => {
    if (!disabled) {
      if (props.type === "single") {
        props.onValueChange?.(newValue as string);
      } else {
        props.onValueChange?.(newValue as string[]);
      }
    }
  };

  return (
    <AccordionPrimitive.Root
      disabled={disabled}
      orientation="vertical"
      onValueChange={handleValueChange}
      className={cx(styles.root, disabled && styles.disabled, fullWidth && styles.fullWidth, className)}
      data-testid="accordion"
      {...restProps}
    >
      {children}
    </AccordionPrimitive.Root>
  );
}

AccordionRoot.displayName = "AccordionRoot";

const AccordionItem = forwardRef<React.ComponentRef<typeof AccordionPrimitive.Item>, AccordionItemProps>(
  ({ children, disabled = false, className, header, ...props }, forwardedRef) => {
    return (
      <AccordionPrimitive.Item
        disabled={disabled}
        className={cx(styles.item, className)}
        data-testid="accordion-item"
        ref={forwardedRef}
        {...props}
      >
        {header && (
          <AccordionHeader>
            <AccordionTrigger>{header}</AccordionTrigger>
          </AccordionHeader>
        )}
        {children}
      </AccordionPrimitive.Item>
    );
  },
);

AccordionItem.displayName = "AccordionItem";

const AccordionHeader = forwardRef<React.ComponentRef<typeof AccordionPrimitive.Header>, AccordionHeaderProps>(
  ({ children, className, ...props }, forwardedRef) => {
    return (
      <AccordionPrimitive.Header
        className={cx(styles.header, className)}
        data-testid="accordion-header"
        ref={forwardedRef}
        {...props}
      >
        {children}
      </AccordionPrimitive.Header>
    );
  },
);

AccordionHeader.displayName = "AccordionHeader";

const AccordionTrigger = forwardRef<React.ComponentRef<typeof AccordionPrimitive.Trigger>, AccordionTriggerProps>(
  ({ children, className, ...props }, forwardedRef) => {
    return (
      <AccordionPrimitive.Trigger
        className={cx(styles.trigger, className)}
        data-testid="accordion-trigger"
        ref={forwardedRef}
        {...props}
      >
        {children}
        <div className={styles.chevron} aria-hidden>
          <IconChevronDown />
        </div>
      </AccordionPrimitive.Trigger>
    );
  },
);

AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = forwardRef<React.ComponentRef<typeof AccordionPrimitive.Content>, AccordionContentProps>(
  ({ children, className, ...props }, forwardedRef) => {
    return (
      <AccordionPrimitive.Content
        className={styles.content}
        data-testid="accordion-content"
        ref={forwardedRef}
        {...props}
      >
        <div data-testid="accordion-content-text" className={cx(styles.contentText, className)}>
          {children}
        </div>
      </AccordionPrimitive.Content>
    );
  },
);

AccordionContent.displayName = "AccordionContent";

// Compound component exports
export const Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  Header: AccordionHeader,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
};

// Individual component exports for flexibility
export { AccordionRoot, AccordionItem, AccordionHeader, AccordionTrigger, AccordionContent };

export default Accordion;
